// components/FilterTabContent.js
import React, { useState } from "react";
import {
  MaturityDateFilter,
  MinMaxCheckFilter,
  MinMaxSelectFilter,
  BooleanFilter,
  FactoredSecuritiesFilter,
} from "./ComplexFilterComponents";
import {
  TypeFilter,
  StateFilter,
  ApplyClearButtons,
  SingleSelectFilter,
  SingleDateFilter,
  IssuerFilter,
  SideFilter,
  BrokerDealerFilter,
  DescriptionFilter,
} from "./BasicFilterComponents";
import { Box } from "@mui/material";
import { tokens } from "../../Common/designTokens"; // added

export function USTreasuryFilters({
  type,
  onTypeChange,
  subTypeOptions,
  side,
  onSideChange,
  brokerDealer,
  onBrokerDealerChange,
  brokerDealerOptions,
  securityCharge,
  onSecurityChargeChange,
  securityChargeOptions,
  maturityMinDate,
  maturityMaxDate,
  onMaturityMinDateChange,
  onMaturityMaxDateChange,
  yieldToWorst,
  onYieldToWorstChange,
  quantity,
  onQuantityChange,
  price,
  onPriceChange,
  coupon,
  onCouponChange,
  onApply,
  onClear,
  onSave,
  isEdit,
  disableApply = false,
  // New props
  maturityPreviewMinDate,
  maturityPreviewMaxDate,
  skipMaturityValidation,
  showYieldCellTag,
}) {
  const [validationErrors, setValidationErrors] = useState({
    maturity: false,
    yieldToWorst: false,
    quantity: false,
    price: false,
    coupon: false,
  });

  const hasErrors = Object.values(validationErrors).some(Boolean);

  return (
    <Box sx={{ color: tokens.colors.fontColour }}>
      <TypeFilter
        value={type}
        onChange={onTypeChange}
        options={subTypeOptions}
      />
      <SideFilter
        value={side}
        onChange={onSideChange}
      />
      <BrokerDealerFilter
        value={brokerDealer}
        onChange={onBrokerDealerChange}
        options={brokerDealerOptions}
      />
      <FactoredSecuritiesFilter
        value={securityCharge}
        onChange={onSecurityChargeChange}
        options={securityChargeOptions}
      />
      <MaturityDateFilter
        minDate={maturityMinDate}
        maxDate={maturityMaxDate}
        onMinDateChange={onMaturityMinDateChange}
        onMaxDateChange={onMaturityMaxDateChange}
        onValidationChange={(hasError) =>
          setValidationErrors((prev) => ({ ...prev, maturity: hasError }))
        }
        // New props for yieldtable preview/behavior
        previewFromDate={maturityPreviewMinDate}
        previewToDate={maturityPreviewMaxDate}
        skipValidation={skipMaturityValidation}
        showYieldCellTag={showYieldCellTag}
      />
      <MinMaxCheckFilter
        label="Yield To Worst"
        check={yieldToWorst.check}
        min={yieldToWorst.min}
        max={yieldToWorst.max}
        onCheckChange={(value) => onYieldToWorstChange("check", value)}
        onMinChange={(value) => onYieldToWorstChange("min", value)}
        onMaxChange={(value) => onYieldToWorstChange("max", value)}
        decimalPlaces={5}
        onValidationChange={(hasError) =>
          setValidationErrors((prev) => ({ ...prev, yieldToWorst: hasError }))
        }
      />
      <MinMaxCheckFilter
        label="Quantity"
        check={quantity.check}
        min={quantity.min}
        max={quantity.max}
        onCheckChange={(value) => onQuantityChange("check", value)}
        onMinChange={(value) => onQuantityChange("min", value)}
        onMaxChange={(value) => onQuantityChange("max", value)}
        onValidationChange={(hasError) =>
          setValidationErrors((prev) => ({ ...prev, quantity: hasError }))
        }
      />
      <MinMaxCheckFilter
        label="Price"
        check={price.check}
        min={price.min}
        max={price.max}
        onCheckChange={(value) => onPriceChange("check", value)}
        onMinChange={(value) => onPriceChange("min", value)}
        onMaxChange={(value) => onPriceChange("max", value)}
        decimalPlaces={5}
        onValidationChange={(hasError) =>
          setValidationErrors((prev) => ({ ...prev, price: hasError }))
        }
      />
      <MinMaxCheckFilter
        label="Coupon"
        check={coupon.check}
        min={coupon.min}
        max={coupon.max}
        onCheckChange={(value) => onCouponChange("check", value)}
        onMinChange={(value) => onCouponChange("min", value)}
        onMaxChange={(value) => onCouponChange("max", value)}
        decimalPlaces={5}
        onValidationChange={(hasError) =>
          setValidationErrors((prev) => ({ ...prev, coupon: hasError }))
        }
      />
      <ApplyClearButtons
        onApply={onApply}
        onClear={onClear}
        onSave={onSave}
        isEdit={isEdit}
        hasValidationErrors={hasErrors}
        disableApply={disableApply}
      />
    </Box>
  );
}

export function CorporatesFilters({
  moodyRatings,
  onMoodyRatingsChange,
  spRatings,
  onSpRatingsChange,
  moodyRatingOptions,
  spRatingOptions,
  description,
  onDescriptionChange,
  issuer,
  onIssuerChange,
  issuerOptions,
  issuerValidationError = false,
  side,
  onSideChange,
  brokerDealer,
  onBrokerDealerChange,
  brokerDealerOptions,
  securityCharge,
  onSecurityChargeChange,
  securityChargeOptions,
  maturityMinDate,
  maturityMaxDate,
  onMaturityMinDateChange,
  onMaturityMaxDateChange,
  yieldToWorst,
  onYieldToWorstChange,
  quantity,
  onQuantityChange,
  price,
  onPriceChange,
  coupon,
  onCouponChange,
  onApply,
  onClear,
  callable,
  onCallableChange,
  paymentFrequency,
  onPaymentFrequencyChange,
  sector,
  onSectorChange,
  nextCallDate,
  onNextCallDateChange,
  paymentFrequencyOptions,
  sectorOptions,
  onSave,
  isEdit,
  disableApply = false,
  // New props
  maturityPreviewMinDate,
  maturityPreviewMaxDate,
  skipMaturityValidation,
  showYieldCellTag,
  // NEW rating preview props
  moodyPreviewFrom,
  moodyPreviewTo,
  spPreviewFrom,
  spPreviewTo,
}) {
  const [validationErrors, setValidationErrors] = useState({
    maturity: false,
    yieldToWorst: false,
    quantity: false,
    price: false,
    coupon: false,
  });

  const hasErrors = Object.values(validationErrors).some(Boolean) || issuerValidationError;

  return (
    <Box sx={{ color: tokens.colors.fontColour }}>
      <DescriptionFilter
        value={description}
        onChange={onDescriptionChange}
      />
      <IssuerFilter
        value={issuer}
        onChange={onIssuerChange}
        options={issuerOptions}
        hasError={issuerValidationError}
      />
      <SideFilter
        value={side}
        onChange={onSideChange}
      />
      <BrokerDealerFilter
        value={brokerDealer}
        onChange={onBrokerDealerChange}
        options={brokerDealerOptions}
      />
      <FactoredSecuritiesFilter
        value={securityCharge}
        onChange={onSecurityChargeChange}
        options={securityChargeOptions}
      />
      <MinMaxSelectFilter
        label="Moody's Ratings"
        options={moodyRatingOptions}
        operator={moodyRatings.operator}
        value={moodyRatings.value}
        onOperatorChange={(value) => onMoodyRatingsChange("operator", value)}
        onValueChange={(value) => onMoodyRatingsChange("value", value)}
        // NEW preview props
        previewFrom={moodyPreviewFrom}
        previewTo={moodyPreviewTo}
        showYieldCellTag={showYieldCellTag}
      />
      <MinMaxSelectFilter
        label="S&P Ratings"
        options={spRatingOptions}
        operator={spRatings.operator}
        value={spRatings.value}
        onOperatorChange={(value) => onSpRatingsChange("operator", value)}
        onValueChange={(value) => onSpRatingsChange("value", value)}
        // NEW preview props
        previewFrom={spPreviewFrom}
        previewTo={spPreviewTo}
        showYieldCellTag={showYieldCellTag}
      />
      <MaturityDateFilter
        minDate={maturityMinDate}
        maxDate={maturityMaxDate}
        onMinDateChange={onMaturityMinDateChange}
        onMaxDateChange={onMaturityMaxDateChange}
        onValidationChange={(hasError) =>
          setValidationErrors((prev) => ({ ...prev, maturity: hasError }))
        }
        // New props for yieldtable preview/behavior
        previewFromDate={maturityPreviewMinDate}
        previewToDate={maturityPreviewMaxDate}
        skipValidation={skipMaturityValidation}
        showYieldCellTag={showYieldCellTag}
      />
      <MinMaxCheckFilter
        label="Yield To Worst"
        check={yieldToWorst.check}
        min={yieldToWorst.min}
        max={yieldToWorst.max}
        onCheckChange={(value) => onYieldToWorstChange("check", value)}
        onMinChange={(value) => onYieldToWorstChange("min", value)}
        onMaxChange={(value) => onYieldToWorstChange("max", value)}
        decimalPlaces={5}
        onValidationChange={(hasError) =>
          setValidationErrors((prev) => ({ ...prev, yieldToWorst: hasError }))
        }
      />
      <MinMaxCheckFilter
        label="Quantity"
        check={quantity.check}
        min={quantity.min}
        max={quantity.max}
        onCheckChange={(value) => onQuantityChange("check", value)}
        onMinChange={(value) => onQuantityChange("min", value)}
        onMaxChange={(value) => onQuantityChange("max", value)}
        onValidationChange={(hasError) =>
          setValidationErrors((prev) => ({ ...prev, quantity: hasError }))
        }
      />
      <MinMaxCheckFilter
        label="Price"
        check={price.check}
        min={price.min}
        max={price.max}
        onCheckChange={(value) => onPriceChange("check", value)}
        onMinChange={(value) => onPriceChange("min", value)}
        onMaxChange={(value) => onPriceChange("max", value)}
        decimalPlaces={5}
        onValidationChange={(hasError) =>
          setValidationErrors((prev) => ({ ...prev, price: hasError }))
        }
      />
      <MinMaxCheckFilter
        label="Coupon"
        check={coupon.check}
        min={coupon.min}
        max={coupon.max}
        onCheckChange={(value) => onCouponChange("check", value)}
        onMinChange={(value) => onCouponChange("min", value)}
        onMaxChange={(value) => onCouponChange("max", value)}
        decimalPlaces={5}
        onValidationChange={(hasError) =>
          setValidationErrors((prev) => ({ ...prev, coupon: hasError }))
        }
      />
      <BooleanFilter
        label="Callable"
        value={callable}
        onChange={(e) => onCallableChange(e.target.value)}
      />
      <SingleSelectFilter
        label="Payment Frequency"
        value={paymentFrequency}
        onChange={(e) => onPaymentFrequencyChange(e.target.value)}
        options={paymentFrequencyOptions}
      />
      <SingleSelectFilter
        label="Sector"
        value={sector}
        onChange={(e) => onSectorChange(e.target.value)}
        options={sectorOptions}
      />
      <SingleDateFilter
        label="Next Call Date"
        date={nextCallDate}
        onDateChange={onNextCallDateChange}
      />
      <ApplyClearButtons
        onApply={onApply}
        onClear={onClear}
        onSave={onSave}
        isEdit={isEdit}
        hasValidationErrors={hasErrors}
        disableApply={disableApply}
      />
    </Box>
  );
}

export function MunicipalsFilters({
  moodyRatings,
  onMoodyRatingsChange,
  spRatings,
  onSpRatingsChange,
  moodyRatingOptions,
  spRatingOptions,
  state,
  onStateChange,
  stateOptions,
  side,
  onSideChange,
  brokerDealer,
  onBrokerDealerChange,
  brokerDealerOptions,
  securityCharge,
  onSecurityChargeChange,
  securityChargeOptions,
  maturityMinDate,
  maturityMaxDate,
  onMaturityMinDateChange,
  onMaturityMaxDateChange,
  yieldToWorst,
  onYieldToWorstChange,
  quantity,
  onQuantityChange,
  price,
  onPriceChange,
  coupon,
  onCouponChange,
  onApply,
  onClear,
  callable,
  onCallableChange,
  paymentFrequency,
  onPaymentFrequencyChange,
  taxable,
  onTaxableChange,
  incomeSource,
  onIncomeSourceChange,
  altMinTax,
  onAltMinTaxChange,
  paymentFrequencyOptions,
  incomeSourceOptions,
  sector, // Add sector prop
  onSectorChange, // Add sector change handler
  sectorOptions, // Add sector options
  onSave,
  isEdit,
  disableApply = false,
  // New props
  maturityPreviewMinDate,
  maturityPreviewMaxDate,
  skipMaturityValidation,
  showYieldCellTag,
  // NEW rating preview props
  moodyPreviewFrom,
  moodyPreviewTo,
  spPreviewFrom,
  spPreviewTo,
}) {
  const [validationErrors, setValidationErrors] = useState({
    maturity: false,
    yieldToWorst: false,
    quantity: false,
    price: false,
    coupon: false,
  });

  const hasErrors = Object.values(validationErrors).some(Boolean);

  return (
    <Box sx={{ color: tokens.colors.fontColour }}>
      <MinMaxSelectFilter
        label="Moody's Ratings"
        options={moodyRatingOptions}
        operator={moodyRatings.operator}
        value={moodyRatings.value}
        onOperatorChange={(value) => onMoodyRatingsChange("operator", value)}
        onValueChange={(value) => onMoodyRatingsChange("value", value)}
        // NEW preview props
        previewFrom={moodyPreviewFrom}
        previewTo={moodyPreviewTo}
        showYieldCellTag={showYieldCellTag}
      />
      <MinMaxSelectFilter
        label="S&P Ratings"
        options={spRatingOptions}
        operator={spRatings.operator}
        value={spRatings.value}
        onOperatorChange={(value) => onSpRatingsChange("operator", value)}
        onValueChange={(value) => onSpRatingsChange("value", value)}
        // NEW preview props
        previewFrom={spPreviewFrom}
        previewTo={spPreviewTo}
        showYieldCellTag={showYieldCellTag}
      />
      <StateFilter
        value={state}
        onChange={onStateChange}
        options={stateOptions}
      />
      <SideFilter
        value={side}
        onChange={onSideChange}
      />
      <BrokerDealerFilter
        value={brokerDealer}
        onChange={onBrokerDealerChange}
        options={brokerDealerOptions}
      />
      <FactoredSecuritiesFilter
        value={securityCharge}
        onChange={onSecurityChargeChange}
        options={securityChargeOptions}
      />
      <MaturityDateFilter
        minDate={maturityMinDate}
        maxDate={maturityMaxDate}
        onMinDateChange={onMaturityMinDateChange}
        onMaxDateChange={onMaturityMaxDateChange}
        onValidationChange={(hasError) =>
          setValidationErrors((prev) => ({ ...prev, maturity: hasError }))
        }
        // New props for yieldtable preview/behavior
        previewFromDate={maturityPreviewMinDate}
        previewToDate={maturityPreviewMaxDate}
        skipValidation={skipMaturityValidation}
        showYieldCellTag={showYieldCellTag}
      />
      <SingleSelectFilter
        label="Payment Frequency"
        value={paymentFrequency}
        onChange={(e) => onPaymentFrequencyChange(e.target.value)}
        options={paymentFrequencyOptions}
      />
      <SingleSelectFilter
        label="Income Source"
        value={incomeSource}
        onChange={(e) => onIncomeSourceChange(e.target.value)}
        options={incomeSourceOptions}
      />
      {/* Add Sector filter here */}
      <SingleSelectFilter
        label="Sector"
        value={sector}
        onChange={(e) => onSectorChange(e.target.value)}
        options={sectorOptions}
      />
      <MinMaxCheckFilter
        label="Yield To Worst"
        check={yieldToWorst.check}
        min={yieldToWorst.min}
        max={yieldToWorst.max}
        onCheckChange={(value) => onYieldToWorstChange("check", value)}
        onMinChange={(value) => onYieldToWorstChange("min", value)}
        onMaxChange={(value) => onYieldToWorstChange("max", value)}
        decimalPlaces={5}
        onValidationChange={(hasError) =>
          setValidationErrors((prev) => ({ ...prev, yieldToWorst: hasError }))
        }
      />
      <MinMaxCheckFilter
        label="Quantity"
        check={quantity.check}
        min={quantity.min}
        max={quantity.max}
        onCheckChange={(value) => onQuantityChange("check", value)}
        onMinChange={(value) => onQuantityChange("min", value)}
        onMaxChange={(value) => onQuantityChange("max", value)}
        onValidationChange={(hasError) =>
          setValidationErrors((prev) => ({ ...prev, quantity: hasError }))
        }
      />
      <MinMaxCheckFilter
        label="Price"
        check={price.check}
        min={price.min}
        max={price.max}
        onCheckChange={(value) => onPriceChange("check", value)}
        onMinChange={(value) => onPriceChange("min", value)}
        onMaxChange={(value) => onPriceChange("max", value)}
        decimalPlaces={5}
        onValidationChange={(hasError) =>
          setValidationErrors((prev) => ({ ...prev, price: hasError }))
        }
      />
      <MinMaxCheckFilter
        label="Coupon"
        check={coupon.check}
        min={coupon.min}
        max={coupon.max}
        onCheckChange={(value) => onCouponChange("check", value)}
        onMinChange={(value) => onCouponChange("min", value)}
        onMaxChange={(value) => onCouponChange("max", value)}
        decimalPlaces={5}
        onValidationChange={(hasError) =>
          setValidationErrors((prev) => ({ ...prev, coupon: hasError }))
        }
      />
      <BooleanFilter
        label="Callable"
        value={callable}
        onChange={(e) => onCallableChange(e.target.value)}
      />
      <BooleanFilter
        label="Taxable"
        value={taxable}
        onChange={(e) => onTaxableChange(e.target.value)}
      />
      <BooleanFilter
        label="Alt Min Tax"
        value={altMinTax}
        onChange={(e) => onAltMinTaxChange(e.target.value)}
      />
      <ApplyClearButtons
        onApply={onApply}
        onClear={onClear}
        onSave={onSave}
        isEdit={isEdit}
        hasValidationErrors={hasErrors}
        disableApply={disableApply}
      />
    </Box>
  );
}
